import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { FiPlusCircle, FiCheck, FiX } from 'react-icons/fi';

const LEAVE_TYPES = ['Sick', 'Casual', 'Annual', 'Maternity', 'Paternity', 'Unpaid'];

const LeaveList = () => {
  const { user } = useAuth();
  const isAdminHR = user.role === 'admin' || user.role === 'hr';
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ leaveType: 'Sick', fromDate: '', toDate: '', reason: '', employee: '' });

  const fetchLeaves = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/leaves');
      setLeaves(res.data);
    } catch (err) { toast.error('Failed to fetch leaves'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLeaves(); }, [fetchLeaves]);

  useEffect(() => {
    if (isAdminHR) {
      api.get('/employees', { params: { limit: 100 } }).then(res => setEmployees(res.data.employees || [])).catch(() => {});
    }
    if (user.employeeId) {
      api.get(`/leaves/balance/${user.employeeId}`).then(res => setBalance(res.data)).catch(() => {});
    }
  }, [isAdminHR, user.employeeId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/leaves', form);
      toast.success('Leave applied');
      setShowForm(false);
      setForm({ leaveType: 'Sick', fromDate: '', toDate: '', reason: '', employee: '' });
      fetchLeaves();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to apply leave'); }
  };

  const handleAction = async (id, action) => {
    const remarks = window.prompt('Remarks (optional):') || '';
    try {
      await api.put(`/leaves/${id}/${action}`, { remarks });
      toast.success(`Leave ${action}d`);
      fetchLeaves();
    } catch (err) { toast.error(`Failed to ${action} leave`); }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>Leave Management</h1>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}><FiPlusCircle /> {showForm ? 'Cancel' : 'Apply Leave'}</button>
      </div>

      {balance && (
        <div className="form-card" style={{ marginBottom: '1rem' }}>
          <h3>Leave Balance</h3>
          <div className="form-grid">
            {['sick', 'casual', 'annual'].map(t => (
              <div key={t} className="detail-item"><label>{t.charAt(0).toUpperCase() + t.slice(1)}</label><span>{balance[t]} days</span></div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="form-card">
          <h3>Apply for Leave</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              {isAdminHR && (
                <div className="form-group"><label>Employee</label>
                  <select name="employee" value={form.employee} onChange={e => setForm({ ...form, employee: e.target.value })}>
                    <option value="">Self</option>
                    {employees.map(emp => <option key={emp._id} value={emp._id}>{emp.name}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group"><label>Leave Type</label>
                <select value={form.leaveType} onChange={e => setForm({ ...form, leaveType: e.target.value })}>
                  {LEAVE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group"><label>From Date</label><input type="date" value={form.fromDate} onChange={e => setForm({ ...form, fromDate: e.target.value })} required /></div>
              <div className="form-group"><label>To Date</label><input type="date" value={form.toDate} onChange={e => setForm({ ...form, toDate: e.target.value })} required /></div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}><label>Reason</label><textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} required rows={3} /></div>
            </div>
            <button type="submit" className="btn-primary" style={{ marginTop: '1rem' }}>Submit</button>
          </form>
        </div>
      )}

      <table className="table" style={{ marginTop: '1rem' }}>
        <thead><tr>{isAdminHR && <th>Employee</th>}<th>Type</th><th>From</th><th>To</th><th>Reason</th><th>Status</th>{isAdminHR && <th>Actions</th>}</tr></thead>
        <tbody>
          {leaves.map(l => (
            <tr key={l._id}>
              {isAdminHR && <td>{l.employee?.name || 'N/A'}</td>}
              <td>{l.leaveType}</td>
              <td>{new Date(l.fromDate).toLocaleDateString()}</td>
              <td>{new Date(l.toDate).toLocaleDateString()}</td>
              <td>{l.reason}</td>
              <td><span className={`status-badge status-${l.status.toLowerCase()}`}>{l.status}</span></td>
              {isAdminHR && <td className="actions">
                {l.status === 'Pending' && (<>
                  <button className="btn-sm btn-success" onClick={() => handleAction(l._id, 'approve')}><FiCheck /></button>
                  <button className="btn-sm btn-danger" onClick={() => handleAction(l._id, 'reject')}><FiX /></button>
                </>)}
              </td>}
            </tr>
          ))}
          {leaves.length === 0 && <tr><td colSpan={isAdminHR ? 7 : 6} style={{ textAlign: 'center' }}>No leave records</td></tr>}
        </tbody>
      </table>
    </div>
  );
};

export default LeaveList;
